import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import {
  BarChart3, Plus, Search, FileText, TrendingUp, Building2, Users,
  Heart, Globe, Shield, Loader2, ArrowRight, Clock, CheckCircle,
  Send, Filter, MoreHorizontal, Trash2, Eye, Sparkles, Settings
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import CotizacionesConfigTab from "../components/CotizacionesConfigTab";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DEPT_CONFIG = {
  salud_local: { label: "Salud Local", icon: Heart, color: "emerald", gradient: "from-emerald-600 to-emerald-800" },
  salud_internacional: { label: "Salud Internacional", icon: Globe, color: "sky", gradient: "from-sky-600 to-sky-800" },
  riesgos_generales: { label: "Riesgos Generales", icon: Shield, color: "amber", gradient: "from-amber-600 to-amber-800" },
};

const ESTADO_CONFIG = {
  borrador: { label: "Borrador", color: "bg-slate-700 text-slate-300" },
  en_proceso: { label: "En Proceso", color: "bg-sky-900/50 text-sky-300" },
  completada: { label: "Completada", color: "bg-emerald-900/50 text-emerald-300" },
  enviada: { label: "Enviada", color: "bg-purple-900/50 text-purple-300" },
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
};

export default function CotizacionesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [estadoFilter, setEstadoFilter] = useState("all");
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState("dashboard");
  const [user, setUser] = useState(null);
  const [newForm, setNewForm] = useState({
    titulo: "", cliente_nombre: "", cliente_rnc: "", departamento: "", ramo: "",
    cliente_email: "", cliente_telefono: "", cantidad_empleados: null, notas: ""
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [cotRes, statsRes, configRes, userRes] = await Promise.all([
        axios.get(`${API}/cotizaciones`, getAuthHeaders()),
        axios.get(`${API}/cotizaciones/stats`, getAuthHeaders()),
        axios.get(`${API}/cotizaciones/config`, getAuthHeaders()),
        axios.get(`${API}/auth/me`, getAuthHeaders()).catch(() => null),
      ]);
      setCotizaciones(cotRes.data?.cotizaciones || []);
      setStats(statsRes.data);
      setConfig(configRes.data);
      if (userRes?.data) setUser(userRes.data);
    } catch (err) {
      console.error(err);
      toast.error("Error cargando datos de cotizaciones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!newForm.titulo || !newForm.cliente_nombre || !newForm.departamento) {
      toast.error("Complete los campos requeridos"); return;
    }
    try {
      setCreating(true);
      const res = await axios.post(`${API}/cotizaciones`, newForm, getAuthHeaders());
      toast.success("Cotización creada exitosamente");
      setShowNewDialog(false);
      navigate(`/cotizaciones/${res.data.id}`);
    } catch (err) {
      toast.error("Error al crear cotización");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar esta cotización?")) return;
    try {
      await axios.delete(`${API}/cotizaciones/${id}`, getAuthHeaders());
      toast.success("Cotización eliminada");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const filteredCotizaciones = cotizaciones.filter(c => {
    if (deptFilter !== "all" && c.departamento !== deptFilter) return false;
    if (estadoFilter !== "all" && c.estado !== estadoFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return c.titulo?.toLowerCase().includes(q) || c.cliente_nombre?.toLowerCase().includes(q);
    }
    return true;
  });

  const ramos = config?.ramos_por_departamento?.[newForm.departamento] || [];
  const allowedDepts = config?.departamentos || Object.keys(DEPT_CONFIG);
  const isAdmin = config?.is_admin || user?.role === "admin" || user?.role === "administrador" || user?.role === "supervisor";

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-950">
        <Sidebar />
        <div className="flex-1 ml-0 md:ml-64 flex items-center justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-950">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-64 overflow-x-hidden">
        <main className="p-4 md:p-6 sm:p-8 pt-14 md:pt-8 max-w-7xl mx-auto overflow-x-hidden" data-testid="cotizaciones-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-50 tracking-tight">
              Cotizaciones
            </h1>
            <p className="text-slate-400 mt-1">Gestión y comparativos de propuestas de seguros</p>
          </div>
          <Button
            onClick={() => {
              const defaultDept = config?.default_departamento || (allowedDepts.length === 1 ? allowedDepts[0] : "");
              setNewForm({ titulo: "", cliente_nombre: "", cliente_rnc: "", departamento: defaultDept, ramo: "", cliente_email: "", cliente_telefono: "", cantidad_empleados: null, notas: "" });
              setShowNewDialog(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl gap-2 px-6"
            data-testid="new-cotizacion-btn"
          >
            <Plus className="w-5 h-5" />
            Nueva Cotización
          </Button>
        </div>

        {/* Main Tabs: Dashboard + Configuración */}
        <Tabs value={activeMainTab} onValueChange={setActiveMainTab}>
          <TabsList className="bg-slate-900 border border-slate-800 rounded-xl mb-6">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-lg gap-1.5" data-testid="tab-dashboard">
              <BarChart3 className="w-4 h-4" /> Dashboard
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="configuracion" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white rounded-lg gap-1.5" data-testid="tab-configuracion">
                <Settings className="w-4 h-4" /> Configuración
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="dashboard">

        {/* KPI Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-900 border-slate-800 shadow-lg hover:shadow-xl hover:border-slate-700 transition-all duration-300 rounded-2xl">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="uppercase tracking-[0.2em] text-xs font-semibold text-slate-400">Total Cotizaciones</p>
                  <p className="text-3xl font-bold text-slate-50 mt-1" data-testid="total-cotizaciones">{stats?.total || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-900/50 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-emerald-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {Object.entries(DEPT_CONFIG).filter(([key]) => allowedDepts.includes(key)).map(([key, cfg]) => {
            const Icon = cfg.icon;
            const deptStats = stats?.por_departamento?.[key];
            return (
              <Card key={key} className="bg-slate-900 border-slate-800 shadow-lg hover:shadow-xl hover:border-slate-700 transition-all duration-300 rounded-2xl">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="uppercase tracking-[0.2em] text-xs font-semibold text-slate-400">{cfg.label}</p>
                      <p className="text-3xl font-bold text-slate-50 mt-1">{deptStats?.total || 0}</p>
                      {deptStats?.completadas > 0 && (
                        <p className="text-xs text-emerald-400 mt-1">{deptStats.completadas} completadas</p>
                      )}
                    </div>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${cfg.gradient} flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar cotización o cliente..."
              className="pl-10 bg-slate-900 border-slate-800 text-slate-200 rounded-xl"
              data-testid="search-cotizaciones"
            />
          </div>
          <Select value={deptFilter} onValueChange={setDeptFilter}>
            <SelectTrigger className="w-48 bg-slate-900 border-slate-800 text-slate-200 rounded-xl">
              <SelectValue placeholder="Departamento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los departamentos</SelectItem>
              {Object.entries(DEPT_CONFIG).filter(([key]) => allowedDepts.includes(key)).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={estadoFilter} onValueChange={setEstadoFilter}>
            <SelectTrigger className="w-40 bg-slate-900 border-slate-800 text-slate-200 rounded-xl">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {Object.entries(ESTADO_CONFIG).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant="outline" className="border-slate-700 text-slate-300 rounded-lg px-3 py-1.5">
            {filteredCotizaciones.length} resultado{filteredCotizaciones.length !== 1 ? "s" : ""}
          </Badge>
        </div>

        {/* Cotizaciones List */}
        {filteredCotizaciones.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800 rounded-2xl">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center mb-4">
                <BarChart3 className="w-8 h-8 text-slate-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-300 mb-2">No hay cotizaciones</h3>
              <p className="text-slate-500 text-sm mb-6">Crea tu primera cotización para comenzar a comparar propuestas</p>
              <Button
                onClick={() => setShowNewDialog(true)}
                className="bg-emerald-600 hover:bg-emerald-500 rounded-xl gap-2"
              >
                <Plus className="w-4 h-4" /> Nueva Cotización
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredCotizaciones.map((cot) => {
              const deptCfg = DEPT_CONFIG[cot.departamento] || {};
              const DeptIcon = deptCfg.icon || FileText;
              const estadoCfg = ESTADO_CONFIG[cot.estado] || {};
              return (
                <Card
                  key={cot.id}
                  className="bg-slate-900 border-slate-800 hover:border-slate-700 hover:-translate-y-0.5 transition-all duration-300 rounded-2xl cursor-pointer group"
                  onClick={() => navigate(`/cotizaciones/${cot.id}`)}
                  data-testid={`cotizacion-row-${cot.id}`}
                >
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${deptCfg.gradient || "from-slate-600 to-slate-800"} flex items-center justify-center flex-shrink-0`}>
                      <DeptIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-100 truncate">{cot.titulo}</h3>
                        <Badge className={`${estadoCfg.color} text-xs rounded-lg`}>{estadoCfg.label}</Badge>
                        {cot.analisis_ai && (
                          <Badge className="bg-sky-900/50 text-sky-300 text-xs rounded-lg gap-1">
                            <Sparkles className="w-3 h-3" /> Analizado
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" /> {cot.cliente_nombre}
                        </span>
                        <span>{deptCfg.label}</span>
                        {cot.ramo && <span className="text-slate-500">{cot.ramo}</span>}
                        <span className="flex items-center gap-1 text-slate-500">
                          <Clock className="w-3 h-3" />
                          {new Date(cot.created_at).toLocaleDateString("es-DO", { day: "2-digit", month: "short" })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right mr-2">
                        <p className="text-xs text-slate-500">Propuestas</p>
                        <p className="text-lg font-bold text-slate-200">{cot.propuestas?.length || 0}</p>
                      </div>
                      <Button variant="ghost" size="icon" className="text-slate-500 hover:text-red-400" onClick={(e) => handleDelete(cot.id, e)} data-testid={`delete-cotizacion-${cot.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <ArrowRight className="w-5 h-5 text-slate-600 group-hover:text-emerald-400 transition-colors" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* New Cotizacion Dialog */}
        </TabsContent>

          {isAdmin && (
            <TabsContent value="configuracion">
              <CotizacionesConfigTab />
            </TabsContent>
          )}
        </Tabs>

        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogContent className="max-w-lg bg-slate-900 border-slate-700 text-slate-100">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-slate-50">
                <Plus className="w-5 h-5 text-emerald-400" />
                Nueva Cotización
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Configure los datos del cliente y seleccione el departamento
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Título *</Label>
                <Input
                  value={newForm.titulo}
                  onChange={(e) => setNewForm({...newForm, titulo: e.target.value})}
                  placeholder="Ej: Cotización Salud Empresa ABC"
                  className="bg-slate-800 border-slate-700 text-slate-100"
                  data-testid="new-cotizacion-titulo"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300">Cliente *</Label>
                  <Input
                    value={newForm.cliente_nombre}
                    onChange={(e) => setNewForm({...newForm, cliente_nombre: e.target.value})}
                    placeholder="Nombre del cliente"
                    className="bg-slate-800 border-slate-700 text-slate-100"
                    data-testid="new-cotizacion-cliente"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">RNC</Label>
                  <Input
                    value={newForm.cliente_rnc}
                    onChange={(e) => setNewForm({...newForm, cliente_rnc: e.target.value})}
                    placeholder="RNC del cliente"
                    className="bg-slate-800 border-slate-700 text-slate-100"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    value={newForm.cliente_email}
                    onChange={(e) => setNewForm({...newForm, cliente_email: e.target.value})}
                    placeholder="correo@empresa.com"
                    className="bg-slate-800 border-slate-700 text-slate-100"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Teléfono</Label>
                  <Input
                    value={newForm.cliente_telefono}
                    onChange={(e) => setNewForm({...newForm, cliente_telefono: e.target.value})}
                    placeholder="809-000-0000"
                    className="bg-slate-800 border-slate-700 text-slate-100"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Departamento *</Label>
                <Select value={newForm.departamento} onValueChange={(v) => setNewForm({...newForm, departamento: v, ramo: ""})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100" data-testid="new-cotizacion-dept">
                    <SelectValue placeholder="Seleccione departamento" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(DEPT_CONFIG).filter(([key]) => allowedDepts.includes(key)).map(([key, cfg]) => (
                      <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {ramos.length > 0 && (
                <div>
                  <Label className="text-slate-300">Ramo</Label>
                  <Select value={newForm.ramo} onValueChange={(v) => setNewForm({...newForm, ramo: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100">
                      <SelectValue placeholder="Seleccione ramo" />
                    </SelectTrigger>
                    <SelectContent>
                      {ramos.map(r => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-slate-300">Notas</Label>
                <Textarea
                  value={newForm.notas}
                  onChange={(e) => setNewForm({...newForm, notas: e.target.value})}
                  placeholder="Notas adicionales..."
                  className="bg-slate-800 border-slate-700 text-slate-100"
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewDialog(false)} className="border-slate-700 text-slate-300">
                Cancelar
              </Button>
              <Button
                onClick={handleCreate}
                disabled={creating || !newForm.titulo || !newForm.cliente_nombre || !newForm.departamento}
                className="bg-emerald-600 hover:bg-emerald-500 rounded-xl gap-2"
                data-testid="create-cotizacion-btn"
              >
                {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                Crear Cotización
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
      </div>
    </div>
  );
}
